// 2D Canvas rendering pass for the top-down engine.
// Pure draw calls — no state mutation. Reads THEMES, COLORS, W/H from levels.js.

import { ghostPositionAt } from "@/lib/replay";
import { COLORS, THEMES, W, H, COLS, ROWS, TILE } from "@/game/levels";

export function render(ctx, state) {
  const theme = THEMES[state.level] || THEMES.outdoor;
  const isOutdoor = state.level === "outdoor";
  const isRooftop = state.level === "rooftop";
  const isHighway = state.level === "highway";
  const isSnow = state.level === "snowstorm";
  const isNeonRain = state.level === "neon_rain";
  const isBeach = state.level === "beach";
  const isDaylight = isOutdoor || isRooftop || isSnow || isBeach;
  const skinColor = state.player.skinColor || COLORS.player;
  const skinGlow = state.player.skinGlow || COLORS.playerGlow;

  // Base backdrop
  ctx.fillStyle = theme.asphaltDark;
  ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = theme.asphalt;
  ctx.fillRect(0, 0, W, H);

  // Highway lane markings
  if (isHighway) {
    const laneCount = 6;
    const laneAreaY0 = 70;
    const laneAreaY1 = H - 90;
    const laneSpan = (laneAreaY1 - laneAreaY0) / laneCount;
    ctx.save();
    ctx.strokeStyle = theme.lane;
    ctx.lineWidth = 2;
    ctx.setLineDash([20, 18]);
    for (let i = 1; i < laneCount; i++) {
      const y = laneAreaY0 + i * laneSpan;
      ctx.beginPath();
      ctx.moveTo(30, y);
      ctx.lineTo(W - 30, y);
      ctx.stroke();
    }
    ctx.setLineDash([]);
    ctx.strokeStyle = "rgba(255,255,255,0.35)";
    ctx.beginPath();
    ctx.moveTo(20, laneAreaY0);
    ctx.lineTo(W - 20, laneAreaY0);
    ctx.moveTo(20, laneAreaY1);
    ctx.lineTo(W - 20, laneAreaY1);
    ctx.stroke();
    ctx.restore();
  }

  // Grid
  ctx.strokeStyle = isOutdoor ? "rgba(255,255,255,0.05)" : "rgba(0,240,255,0.04)";
  ctx.lineWidth = 1;
  for (let x = 0; x <= COLS; x++) {
    ctx.beginPath();
    ctx.moveTo(x * TILE, 0);
    ctx.lineTo(x * TILE, H);
    ctx.stroke();
  }
  for (let y = 0; y <= ROWS; y++) {
    ctx.beginPath();
    ctx.moveTo(0, y * TILE);
    ctx.lineTo(W, y * TILE);
    ctx.stroke();
  }

  // Parking bay lines (skip on highway which has its own markings)
  if (!isHighway) {
    ctx.strokeStyle = theme.lane;
    ctx.lineWidth = 2;
    const startX = 40;
    const bayInnerGap = 22;
    const carW = 30;
    const bayWidth = carW * 2 + bayInnerGap;
    const aisleWidth = isOutdoor ? 68 : 64;
    const sectionWidth = bayWidth + aisleWidth;
    const bays = Math.floor((W - startX * 2) / sectionWidth);
    for (let b = 0; b < bays; b++) {
      const bayX = startX + b * sectionWidth;
      ctx.setLineDash([8, 8]);
      ctx.beginPath();
      ctx.moveTo(bayX + carW + bayInnerGap / 2, 30);
      ctx.lineTo(bayX + carW + bayInnerGap / 2, H - 30);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.beginPath();
      ctx.moveTo(bayX - 3, 30);
      ctx.lineTo(bayX - 3, H - 30);
      ctx.moveTo(bayX + bayWidth + 3, 30);
      ctx.lineTo(bayX + bayWidth + 3, H - 30);
      ctx.stroke();
    }
    ctx.setLineDash([]);
    ctx.lineWidth = 1;
  }

  // Walls (palm trees on beach get a distinct look)
  for (const w of state.walls) {
    if (w.palm) {
      // Trunk shadow
      ctx.fillStyle = "rgba(80,50,20,0.35)";
      ctx.beginPath();
      ctx.ellipse(w.x + w.w / 2 + 6, w.y + w.h + 4, w.w * 0.9, 6, 0, 0, Math.PI * 2);
      ctx.fill();
      // Trunk
      ctx.fillStyle = "#7a4a1e";
      ctx.fillRect(w.x + w.w / 2 - 3, w.y + 6, 6, w.h - 6);
      // Fronds
      ctx.fillStyle = "#2e7a3a";
      const cx = w.x + w.w / 2;
      const cy = w.y + 6;
      for (let i = 0; i < 6; i++) {
        const a = (i / 6) * Math.PI * 2;
        ctx.beginPath();
        ctx.ellipse(cx + Math.cos(a) * 10, cy + Math.sin(a) * 5, 12, 5, a, 0, Math.PI * 2);
        ctx.fill();
      }
      // Coconut cluster
      ctx.fillStyle = "#3a1e0a";
      ctx.beginPath();
      ctx.arc(cx, cy, 3, 0, Math.PI * 2);
      ctx.fill();
      continue;
    }
    ctx.fillStyle = isOutdoor ? "#2f333a" : COLORS.wall;
    ctx.fillRect(w.x, w.y, w.w, w.h);
    ctx.strokeStyle = isOutdoor ? "#454a52" : COLORS.wallEdge;
    ctx.strokeRect(w.x + 0.5, w.y + 0.5, w.w - 1, w.h - 1);
  }

  // Exits
  for (const e of state.exits) {
    ctx.save();
    ctx.shadowColor = COLORS.exitGlow;
    ctx.shadowBlur = 24;
    ctx.fillStyle = COLORS.exit;
    ctx.fillRect(e.x, e.y, e.w, e.h);
    ctx.restore();
    ctx.fillStyle = "#000";
    ctx.font = "bold 10px 'JetBrains Mono', monospace";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("EXIT", e.x + e.w / 2, e.y + e.h / 2);
  }

  // Parked cars — vertical sprites with shadows
  for (const o of state.obstacles) {
    ctx.fillStyle = theme.shadowColor;
    ctx.fillRect(o.x + theme.shadowOffset.x, o.y + theme.shadowOffset.y, o.w, o.h);

    ctx.fillStyle = o.color || COLORS.parked;
    ctx.fillRect(o.x, o.y, o.w, o.h);
    ctx.strokeStyle = COLORS.parkedEdge;
    ctx.strokeRect(o.x + 0.5, o.y + 0.5, o.w - 1, o.h - 1);

    const facing = o.facing === "left" ? -1 : 1;
    ctx.fillStyle = "rgba(180,220,255,0.18)";
    ctx.fillRect(o.x + 4, o.y + 6, o.w - 8, 10);
    ctx.fillStyle = "rgba(180,220,255,0.10)";
    ctx.fillRect(o.x + 4, o.y + o.h - 16, o.w - 8, 10);
    ctx.fillStyle = "rgba(0,0,0,0.25)";
    ctx.fillRect(o.x + o.w / 2 - 1, o.y + 18, 2, o.h - 36);
    if (isOutdoor) {
      ctx.fillStyle = "rgba(255,255,255,0.6)";
      const hy = facing === -1 ? o.y + 3 : o.y + o.h - 5;
      ctx.fillRect(o.x + 4, hy, 3, 2);
      ctx.fillRect(o.x + o.w - 7, hy, 3, 2);
    }
  }

  // Power-ups
  for (const pw of state.powers) {
    ctx.save();
    ctx.shadowColor = COLORS.powerGlow;
    ctx.shadowBlur = 18;
    const pulse = 0.6 + Math.sin(state.time * 6) * 0.4;
    ctx.globalAlpha = pulse;
    ctx.fillStyle = COLORS.power;
    ctx.beginPath();
    ctx.arc(pw.x + pw.w / 2, pw.y + pw.h / 2, pw.w / 2, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
    ctx.fillStyle = "#000";
    ctx.font = "bold 10px 'JetBrains Mono', monospace";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    const letter = pw.kind === "shield" ? "S" : pw.kind === "speed" ? "»" : "F";
    ctx.fillText(letter, pw.x + pw.w / 2, pw.y + pw.h / 2);
  }

  // Enemies
  for (const e of state.enemies) {
    ctx.save();
    ctx.translate(e.x + e.w / 2 + theme.shadowOffset.x * 0.6, e.y + e.h / 2 + theme.shadowOffset.y * 0.6);
    ctx.rotate(e.angle);
    ctx.fillStyle = theme.shadowColor;
    ctx.fillRect(-e.w / 2, -e.h / 2, e.w, e.h);
    ctx.restore();

    ctx.save();
    ctx.translate(e.x + e.w / 2, e.y + e.h / 2);
    ctx.rotate(e.angle);
    // Buggies (beach boss) use their own color + a warning glow
    const buggy = e.buggy;
    ctx.shadowColor = buggy ? "rgba(255,138,0,0.85)" : COLORS.enemyGlow;
    ctx.shadowBlur = buggy ? 22 : (isOutdoor ? 8 : 18);
    ctx.fillStyle = buggy ? "#FF8A00" : COLORS.enemy;
    ctx.fillRect(-e.w / 2, -e.h / 2, e.w, e.h);
    // Roll cage / roof rails on buggy
    if (buggy) {
      ctx.strokeStyle = "#3a1a00";
      ctx.lineWidth = 1.5;
      ctx.strokeRect(-e.w / 2 + 2, -e.h / 2 + 2, e.w - 4, e.h - 4);
      ctx.fillStyle = "#111";
      ctx.fillRect(-e.w / 2 + 4, -e.h / 2 + 4, e.w - 8, 4); // windshield
    }
    ctx.shadowBlur = 0;
    ctx.fillStyle = "#fff";
    ctx.fillRect(e.w / 2 - 3, -e.h / 2 + 2, 3, 4);
    ctx.fillRect(e.w / 2 - 3, e.h / 2 - 6, 3, 4);
    if (!isOutdoor) {
      const grad = ctx.createLinearGradient(e.w / 2, 0, e.w / 2 + 90, 0);
      grad.addColorStop(0, "rgba(255,240,180,0.35)");
      grad.addColorStop(1, "rgba(255,240,180,0)");
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.moveTo(e.w / 2, -e.h / 2);
      ctx.lineTo(e.w / 2 + 90, -e.h / 2 - 24);
      ctx.lineTo(e.w / 2 + 90, e.h / 2 + 24);
      ctx.lineTo(e.w / 2, e.h / 2);
      ctx.closePath();
      ctx.fill();
    }
    ctx.restore();
  }

  // Ghost silhouette (daily challenge)
  if (state.ghostReplay && state.ghostReplay.length > 0) {
    const g = ghostPositionAt(state.ghostReplay, state.time);
    if (g) {
      const prismaticGhost = state.player.handleGem === "prismatic";
      ctx.save();
      if (prismaticGhost) {
        // Rainbow tinted ghost — hue cycles at 90°/s
        const hue = (state.time * 90) % 360;
        ctx.globalAlpha = 0.55;
        ctx.shadowColor = `hsla(${hue}, 100%, 65%, 0.9)`;
        ctx.shadowBlur = 14;
        ctx.fillStyle = `hsla(${hue}, 100%, 72%, 0.95)`;
        ctx.beginPath();
        ctx.arc(g.x + 7, g.y + 7, 7, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = 0.3;
        ctx.fillStyle = `hsla(${(hue + 60) % 360}, 100%, 60%, 0.7)`;
        ctx.beginPath();
        ctx.arc(g.x + 7, g.y + 7, 13, 0, Math.PI * 2);
        ctx.fill();
      } else {
        ctx.globalAlpha = 0.35;
        ctx.shadowColor = "rgba(255,255,255,0.6)";
        ctx.shadowBlur = 10;
        ctx.fillStyle = "rgba(255,255,255,0.9)";
        ctx.beginPath();
        ctx.arc(g.x + 7, g.y + 7, 7, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = 0.18;
        ctx.beginPath();
        ctx.arc(g.x + 7, g.y + 7, 12, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
    }
  }

  // Player
  const p = state.player;
  const nowMs = state.time * 1000;
  const shielded = nowMs < p.invincibleUntil;
  const boosted = nowMs < p.speedBoostUntil;

  // ---- STORM RIDER CAPE ----
  // Skin-driven trail: when skin is Storm Rider (color #7DD3FC-ish) and weather
  // is on, draw a fading cape/tail behind the player. We keep a small position
  // history in state.player.trail (populated below).
  const isStormRider = state.player.skinColor === "#7DD3FC";
  if (isStormRider && state.weather && state.weather.type !== "off") {
    if (!state.player.trail) state.player.trail = [];
    const t = state.player.trail;
    t.push({ x: p.x + p.w / 2, y: p.y + p.h / 2, tm: state.time });
    while (t.length > 18) t.shift();
    // Cull ancient points
    while (t.length && state.time - t[0].tm > 0.75) t.shift();
    ctx.save();
    for (let i = 0; i < t.length - 1; i++) {
      const alpha = (i / t.length) * 0.55;
      const width = 2 + (i / t.length) * 6;
      ctx.strokeStyle = `rgba(125,211,252,${alpha.toFixed(3)})`;
      ctx.lineWidth = width;
      ctx.lineCap = "round";
      ctx.shadowColor = "rgba(125,211,252,0.7)";
      ctx.shadowBlur = 10;
      ctx.beginPath();
      ctx.moveTo(t[i].x, t[i].y);
      ctx.lineTo(t[i + 1].x, t[i + 1].y);
      ctx.stroke();
    }
    ctx.restore();
  }

  // ---- DUELIST AURA FX ----
  // Magenta comet-tail behind the fugitive for DUELIST-title carriers.
  if (state.player.duelist && Array.isArray(state.player.trail) && state.player.trail.length > 1) {
    ctx.save();
    ctx.globalCompositeOperation = "lighter";
    const trail = state.player.trail;
    const n = trail.length;
    for (let i = 0; i < n - 1; i++) {
      const a = trail[i];
      const b = trail[i + 1];
      const alpha = (i / n) * 0.55; // older = fainter
      const r = 2 + (i / n) * 5;    // older = thinner
      ctx.strokeStyle = `rgba(255,110,199,${alpha.toFixed(3)})`;
      ctx.lineWidth = r;
      ctx.lineCap = "round";
      ctx.shadowColor = "rgba(255,110,199,0.9)";
      ctx.shadowBlur = 10;
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.stroke();
    }
    ctx.restore();
  }

  // ---- PRISMATIC AURA FX ----
  // When the ◈ Prismatic gem is equipped, orbit a shimmering rainbow ring
  // around the player. Purely cosmetic — 3 orbiting sparkles + inner halo.
  if (state.player.handleGem === "prismatic") {
    const cx = p.x + p.w / 2;
    const cy = p.y + p.h / 2;
    const t = state.time;
    ctx.save();
    // Rotating hue-cycling halo
    const haloR = 18 + Math.sin(t * 3) * 1.6;
    const halo = ctx.createRadialGradient(cx, cy, haloR * 0.35, cx, cy, haloR);
    const hueBase = (t * 90) % 360;
    halo.addColorStop(0, `hsla(${hueBase}, 100%, 70%, 0.0)`);
    halo.addColorStop(0.55, `hsla(${(hueBase + 60) % 360}, 100%, 65%, 0.35)`);
    halo.addColorStop(1, `hsla(${(hueBase + 180) % 360}, 100%, 55%, 0)`);
    ctx.globalCompositeOperation = "lighter";
    ctx.fillStyle = halo;
    ctx.beginPath();
    ctx.arc(cx, cy, haloR, 0, Math.PI * 2);
    ctx.fill();

    // 3 orbiting sparkles, 120° apart, radius pulses ±2px
    const orbitR = 16 + Math.sin(t * 4) * 2;
    for (let i = 0; i < 3; i++) {
      const a = t * 2.2 + (i * Math.PI * 2) / 3;
      const sx = cx + Math.cos(a) * orbitR;
      const sy = cy + Math.sin(a) * orbitR;
      const hue = (t * 120 + i * 120) % 360;
      ctx.shadowColor = `hsla(${hue}, 100%, 65%, 0.9)`;
      ctx.shadowBlur = 12;
      ctx.fillStyle = `hsla(${hue}, 100%, 72%, 0.95)`;
      ctx.beginPath();
      ctx.arc(sx, sy, 2.4, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }

  ctx.save();
  ctx.translate(p.x + p.w / 2, p.y + p.h / 2);
  ctx.rotate(p.angle);
  ctx.shadowColor = shielded ? "rgba(57,255,20,0.9)" : skinGlow;
  ctx.shadowBlur = shielded ? 22 : 14;
  ctx.fillStyle = shielded ? COLORS.exit : skinColor;
  ctx.beginPath();
  ctx.moveTo(p.w / 2, 0);
  ctx.lineTo(-p.w / 2, -p.h / 2);
  ctx.lineTo(-p.w / 2 + 4, 0);
  ctx.lineTo(-p.w / 2, p.h / 2);
  ctx.closePath();
  ctx.fill();
  ctx.restore();

  if (boosted) {
    ctx.save();
    ctx.globalAlpha = 0.4;
    ctx.fillStyle = skinColor;
    ctx.beginPath();
    ctx.arc(p.x + p.w / 2 - Math.cos(p.angle) * 12, p.y + p.h / 2 - Math.sin(p.angle) * 12, 6, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  // Wind arrow (rooftop or weather=wind)
  const weather = state.weather;
  const showWindArrow = (isRooftop || (weather && weather.type === "wind")) && state.wind && state.wind.strength > 4;
  if (showWindArrow) {
    const cx = p.x + p.w / 2;
    const cy = p.y + p.h / 2 - 28;
    const len = Math.min(30, state.wind.strength * 0.55);
    const angle = Math.atan2(state.wind.y, state.wind.x);
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(angle);
    ctx.strokeStyle = "rgba(255,200,120,0.85)";
    ctx.fillStyle = "rgba(255,200,120,0.85)";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(-len / 2, 0);
    ctx.lineTo(len / 2, 0);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(len / 2, 0);
    ctx.lineTo(len / 2 - 6, -4);
    ctx.lineTo(len / 2 - 6, 4);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }

  // ===== LIGHTING PASS =====
  if (isSnow) {
    drawSnowOverlay(ctx, state);
  } else if (isNeonRain) {
    drawNeonRainOverlay(ctx, state, theme);
  } else if (isHighway) {
    drawHighwayGlow(ctx, state);
  } else if (isDaylight) {
    drawDaylight(ctx, isRooftop, isBeach, theme);
  } else {
    drawUndergroundLighting(ctx, state, theme);
  }

  // ===== WEATHER OVERLAY (on top of lighting) =====
  if (weather) {
    if (weather.type === "rain") drawRainWeather(ctx, state, weather.intensity || 0.6);
    else if (weather.type === "fog") drawFogWeather(ctx, state, weather.intensity || 0.6);
    else if (weather.type === "wind") drawWindWeather(ctx, state, weather.intensity || 0.6);
  }
}

// -------- Lighting / overlay helpers --------
function drawSnowOverlay(ctx, state) {
  ctx.save();
  ctx.fillStyle = "rgba(230,240,255,0.18)";
  ctx.fillRect(0, 0, W, H);
  const flakeCount = 90;
  for (let i = 0; i < flakeCount; i++) {
    const seed = i * 9301 + 49297;
    const bx = (seed % W);
    const by = ((seed * 233) % H);
    const speedY = 60 + (i % 5) * 20;
    const speedX = 30 + (i % 3) * 8;
    const t = state.time;
    const x = (bx + t * speedX) % (W + 20) - 10;
    const y = (by + t * speedY) % (H + 20) - 10;
    const r = 1.2 + (i % 3) * 0.6;
    ctx.fillStyle = "rgba(255,255,255,0.9)";
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

function drawNeonRainOverlay(ctx, state, theme) {
  ctx.save();
  ctx.globalCompositeOperation = "multiply";
  ctx.fillStyle = "rgba(25,10,40,0.45)";
  ctx.fillRect(0, 0, W, H);
  ctx.restore();
  ctx.save();
  ctx.globalCompositeOperation = "lighter";
  const lamps = theme.lamps || [];
  for (const l of lamps) {
    const lg = ctx.createRadialGradient(l.x, l.y, 0, l.x, l.y, 160);
    lg.addColorStop(0, l.hue + "cc");
    lg.addColorStop(0.35, l.hue + "44");
    lg.addColorStop(1, l.hue + "00");
    ctx.fillStyle = lg;
    ctx.beginPath();
    ctx.arc(l.x, l.y, 160, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
  ctx.save();
  ctx.strokeStyle = "rgba(200,220,255,0.35)";
  ctx.lineWidth = 1;
  const rainCount = 60;
  for (let i = 0; i < rainCount; i++) {
    const seed = (i * 73.31 + state.time * 260) % (W + 40);
    const x = ((seed) % W) - 10;
    const y = ((i * 137.5 + state.time * 420) % (H + 20));
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x - 4, y + 14);
    ctx.stroke();
  }
  ctx.restore();
}

function drawHighwayGlow(ctx, state) {
  ctx.save();
  ctx.globalCompositeOperation = "multiply";
  ctx.fillStyle = "rgba(20,24,36,0.35)";
  ctx.fillRect(0, 0, W, H);
  ctx.restore();
  ctx.save();
  ctx.globalCompositeOperation = "lighter";
  for (const e of state.enemies) {
    const gx = e.x + e.w / 2;
    const gy = e.y + e.h / 2;
    const rg = ctx.createRadialGradient(gx, gy, 0, gx, gy, 90);
    rg.addColorStop(0, "rgba(255,100,120,0.28)");
    rg.addColorStop(0.5, "rgba(255,100,120,0.1)");
    rg.addColorStop(1, "rgba(255,100,120,0)");
    ctx.fillStyle = rg;
    ctx.beginPath();
    ctx.arc(gx, gy, 90, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

function drawDaylight(ctx, isRooftop, isBeach, theme) {
  const g = ctx.createRadialGradient(W * 0.85, H * 0.1, 40, W * 0.5, H * 0.5, W);
  g.addColorStop(0, theme.sunColor);
  if (isBeach) {
    g.addColorStop(0.5, "rgba(255,200,120,0.10)");
    g.addColorStop(1, "rgba(255,200,120,0)");
  } else if (isRooftop) {
    g.addColorStop(0.5, "rgba(255,150,80,0.08)");
    g.addColorStop(1, "rgba(255,150,80,0)");
  } else {
    g.addColorStop(0.5, "rgba(255,220,140,0.06)");
    g.addColorStop(1, "rgba(255,220,140,0)");
  }
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, W, H);
  ctx.save();
  ctx.globalCompositeOperation = "screen";
  const flare = ctx.createRadialGradient(W * 0.88, H * 0.12, 0, W * 0.88, H * 0.12, 80);
  if (isBeach) {
    flare.addColorStop(0, "rgba(255,220,150,0.8)");
    flare.addColorStop(1, "rgba(255,220,150,0)");
  } else if (isRooftop) {
    flare.addColorStop(0, "rgba(255,180,120,0.75)");
    flare.addColorStop(1, "rgba(255,180,120,0)");
  } else {
    flare.addColorStop(0, "rgba(255,240,180,0.7)");
    flare.addColorStop(1, "rgba(255,240,180,0)");
  }
  ctx.fillStyle = flare;
  ctx.beginPath();
  ctx.arc(W * 0.88, H * 0.12, 80, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

function drawUndergroundLighting(ctx, state, theme) {
  ctx.save();
  ctx.globalCompositeOperation = "multiply";
  ctx.fillStyle = "rgba(10,10,14,0.78)";
  ctx.fillRect(0, 0, W, H);
  ctx.restore();

  ctx.save();
  ctx.globalCompositeOperation = "lighter";
  for (const cl of theme.ceilingLights) {
    const lg = ctx.createRadialGradient(cl.x, cl.y, 0, cl.x, cl.y, 130);
    lg.addColorStop(0, "rgba(255,235,180,0.55)");
    lg.addColorStop(0.4, "rgba(255,235,180,0.2)");
    lg.addColorStop(1, "rgba(255,235,180,0)");
    ctx.fillStyle = lg;
    ctx.beginPath();
    ctx.arc(cl.x, cl.y, 130, 0, Math.PI * 2);
    ctx.fill();
  }
  const p = state.player;
  const px = p.x + p.w / 2;
  const py = p.y + p.h / 2;
  const rad = theme.flashlightRadius;
  const flash = ctx.createRadialGradient(px, py, 0, px, py, rad);
  flash.addColorStop(0, "rgba(0,240,255,0.5)");
  flash.addColorStop(0.5, "rgba(0,240,255,0.18)");
  flash.addColorStop(1, "rgba(0,240,255,0)");
  ctx.fillStyle = flash;
  ctx.beginPath();
  ctx.arc(px, py, rad, 0, Math.PI * 2);
  ctx.fill();
  for (const e of state.exits) {
    const cx = e.x + e.w / 2;
    const cy = e.y + e.h / 2;
    const eg = ctx.createRadialGradient(cx, cy, 0, cx, cy, 90);
    eg.addColorStop(0, "rgba(57,255,20,0.55)");
    eg.addColorStop(1, "rgba(57,255,20,0)");
    ctx.fillStyle = eg;
    ctx.beginPath();
    ctx.arc(cx, cy, 90, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();

  for (const cl of theme.ceilingLights) {
    ctx.fillStyle = "#fff6c8";
    ctx.beginPath();
    ctx.arc(cl.x, cl.y, 3, 0, Math.PI * 2);
    ctx.fill();
  }
}

// -------- Weather overlays (Task 2) --------
function drawRainWeather(ctx, state, intensity) {
  // Diagonal falling rain streaks + slight desaturating haze
  ctx.save();
  ctx.fillStyle = `rgba(120,140,170,${0.14 * intensity})`;
  ctx.fillRect(0, 0, W, H);
  ctx.strokeStyle = `rgba(200,220,255,${0.35 * intensity})`;
  ctx.lineWidth = 1;
  const drops = Math.floor(90 * intensity);
  for (let i = 0; i < drops; i++) {
    const x = ((i * 91.3 + state.time * 340) % (W + 40)) - 20;
    const y = ((i * 137.5 + state.time * 620) % (H + 40)) - 20;
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x - 3, y + 12);
    ctx.stroke();
  }
  ctx.restore();
}

function drawFogWeather(ctx, state, intensity) {
  // Radial vignette + drifting fog bands. Reduces visibility away from player.
  const p = state.player;
  const px = p.x + p.w / 2;
  const py = p.y + p.h / 2;
  const visRadius = 220 - 90 * intensity; // stronger fog = smaller vis
  ctx.save();
  const grad = ctx.createRadialGradient(px, py, visRadius * 0.55, px, py, visRadius);
  grad.addColorStop(0, "rgba(200,205,215,0)");
  grad.addColorStop(1, `rgba(200,205,215,${0.72 * intensity})`);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, H);
  // Drifting fog bands
  ctx.globalCompositeOperation = "screen";
  ctx.fillStyle = `rgba(240,245,250,${0.06 * intensity})`;
  for (let i = 0; i < 4; i++) {
    const y = ((i * 160 + state.time * 22 * (i + 1)) % (H + 80)) - 40;
    ctx.beginPath();
    ctx.ellipse(W / 2, y, W * 0.9, 40, 0, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

function drawWindWeather(ctx, state, intensity) {
  // Streaky debris/dust particles moving in wind direction
  const wind = state.wind || { x: 1, y: 0, strength: 30 };
  const wx = wind.x;
  const wy = wind.y;
  ctx.save();
  ctx.strokeStyle = `rgba(255,220,180,${0.25 * intensity})`;
  ctx.lineWidth = 1;
  const streaks = Math.floor(50 * intensity);
  for (let i = 0; i < streaks; i++) {
    const bx = (i * 173.1 + state.time * 260 * intensity) % (W + 80) - 40;
    const by = (i * 91.5 + state.time * 140 * intensity) % (H + 80) - 40;
    const len = 8 + (i % 5) * 3;
    ctx.beginPath();
    ctx.moveTo(bx, by);
    ctx.lineTo(bx - wx * len, by - wy * len);
    ctx.stroke();
  }
  ctx.restore();
}
